import { ReactNode, useEffect } from 'react';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import axios from 'axios';
import { useNetworkStatusObserver } from 'hooks/UseNetworkStatusObserver';
import { Link } from 'components/Toolkit/Button/Link';

import { StandardHeader } from 'components/Layouts/Headers/StandardHeader';
import { Footer } from 'components/Layouts/Footer/Footer';
import { useUserContext } from 'contexts/UserContext';
import { ThemeTypes } from 'types/Theme.namespace';
import { GlobalSearchModalProps } from 'components/PageWrappers/SharedComponents/GlobalSearchModal/GlobalSearchModal';
import type { VerificationComponentsThatAreNotInitiallyVisibleProps } from 'components/Layouts/components/VerificationComponentsThatAreNotInitiallyVisible';
import { inAppMessagingApi } from 'api/inAppMessagingApi';
import { Notifications, IUnreadInAppMessageData } from 'types';
import { fireToast } from 'helpers/Toasts';
import getConfig from 'next/config';

const CommonLayoutUserComponents = dynamic<{ cdnUrl: string }>(
  () =>
    import('./components/CommonLayoutUserComponents').then(
      (mod) => mod.CommonLayoutUserComponents,
    ),
  { ssr: false },
);

const GlobalSearchModal = dynamic<GlobalSearchModalProps>(
  () =>
    import(
      /* webpackChunkName: "GlobalSearchModal" */ 'components/PageWrappers/SharedComponents/GlobalSearchModal/GlobalSearchModal'
    ).then((mod) => mod.GlobalSearchModal),
  { ssr: false },
);

const VerificationComponentsThatAreNotInitiallyVisible =
  dynamic<VerificationComponentsThatAreNotInitiallyVisibleProps>(
    () =>
      import(
        './components/VerificationComponentsThatAreNotInitiallyVisible'
      ).then((mod) => mod.VerificationComponentsThatAreNotInitiallyVisible),
    { ssr: false },
  );

const InAppMessagingComponents = dynamic<{
  inAppMessageData: IUnreadInAppMessageData;
  cdnUrl: string;
}>(
  () =>
    import('./components/InAppMessagingComponents').then(
      (mod) => mod.InAppMessagingComponents,
    ),
  { ssr: false },
);

type HeaderVariant = 'DEFAULT' | 'TRANSPARENT';
interface DefaultLayoutProps {
  children: ReactNode;
  domain: string;
  bg?: ThemeTypes.Colors;
  headerVariant?: HeaderVariant;
  headerHasShadow?: boolean;
  headerHasSpacer?: boolean;
  notificationData?: Notifications;
  unreadInAppMessage?: string;
  vertical?: string;
}

const Wrapper = styled.div<{ bg: ThemeTypes.Colors }>`
  ${({ theme, bg }) => bg && `background-color: ${theme.colors[bg]}`}
`;

const {
  publicRuntimeConfig: { CDN_STATIC_ASSETS, DATA_REQUESTS_URL },
} = getConfig();

function DefaultLayout({
  children,
  domain = '',
  bg = 'OFFWHITE',
  headerVariant = 'DEFAULT',
  headerHasShadow = false,
  headerHasSpacer = false,
  notificationData = {
    unreadHistoryChecks: 0,
    unreadMessages: 0,
    totalUnread: 0,
  },
  unreadInAppMessage,
  vertical,
}: DefaultLayoutProps) {
  const {
    user,
    ddRecommendedUser,
    consentOptions,
    setIsInAppMessageSimpleModalOpen,
    setInAppMessageData,
    inAppMessageData,
  } = useUserContext();

  useNetworkStatusObserver({
    onOffline: () => {
      fireToast({
        type: 'ERROR',
        text: (
          <>
            No internet connection?{' '}
            {typeof window !== 'undefined' ? (
              <Link ofType="SECONDARY" href={window.location.href} inline>
                Please refresh your page
              </Link>
            ) : undefined}
          </>
        ),
      });
    },
  });

  useEffect(() => {
    // remove cookie if consent is revoked
    if (consentOptions?.functional === false) {
      document.cookie =
        'ddRecommendedUser=; expires=Mon, 01 Jan 2001 00:00:00 GMT; path=/;';
    } else {
      document.cookie = `ddRecommendedUser=${ddRecommendedUser}; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/;`;
    }
  }, [ddRecommendedUser, consentOptions]);

  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();
    const fetchMessageData = async function () {
      if (unreadInAppMessage === 'true') {
        try {
          const { data } = await inAppMessagingApi.getUnreadInAppMessageData(
            cancelTokenSource,
          );
          setIsInAppMessageSimpleModalOpen(true);
          setInAppMessageData(data);
        } catch (_error) {}
      }
    };
    fetchMessageData();

    /*
     * Ensure modal does not appear and api call is cancelled
     * after user has navigated away from the page
     */
    return () => {
      setIsInAppMessageSimpleModalOpen(false);
      setInAppMessageData(undefined);
      cancelTokenSource.cancel();
    };
  }, [unreadInAppMessage]);

  return (
    <Wrapper bg={bg}>
      <StandardHeader
        baseUrl={domain}
        cdnUrl={CDN_STATIC_ASSETS}
        username={user?.name}
        variant={headerVariant}
        headerHasShadow={headerHasShadow}
        headerHasSpacer={headerHasSpacer}
        notificationCount={notificationData.totalUnread}
        unreadMessageCount={notificationData.unreadMessages}
        unreadHistoryCheckCount={notificationData.unreadHistoryChecks}
        vertical={vertical}
      />

      <GlobalSearchModal baseUrl={domain} cdnUrl={CDN_STATIC_ASSETS} />

      {unreadInAppMessage === 'true' && inAppMessageData && (
        <InAppMessagingComponents
          cdnUrl={CDN_STATIC_ASSETS}
          inAppMessageData={inAppMessageData}
        />
      )}

      <CommonLayoutUserComponents cdnUrl={CDN_STATIC_ASSETS} />
      {user && (
        <VerificationComponentsThatAreNotInitiallyVisible
          cdnUrl={CDN_STATIC_ASSETS}
        />
      )}
      {children}
      <Footer
        baseUrl={domain}
        cdnUrl={CDN_STATIC_ASSETS}
        isLoggedIn={Boolean(user?.name)}
        dataRequestsUrl={DATA_REQUESTS_URL}
      />
    </Wrapper>
  );
}

export default DefaultLayout;
